import { Card, Descriptions, Spin, Tag } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { apiClient } from '@/api/client';

type HealthResponse = {
  status: 'healthy' | 'degraded' | 'unhealthy';
  services: Record<string, 'healthy' | 'degraded' | 'unhealthy' | 'unknown'>;
  version: string;
  timestamp: string;
};

const FRONTEND_BUILD = {
  version: import.meta.env.VITE_BUILD_VERSION ?? 'local',
  commit: import.meta.env.VITE_BUILD_COMMIT ?? 'local',
};

export function HealthPage() {
  const { t } = useTranslation();
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<HealthResponse>('/health')
      .then((res) => {
        if (!cancelled) setHealth(res.data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unknown error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Card title={t('health.title')} style={{ maxWidth: 720 }}>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label={t('health.frontendVersion')}>
          {FRONTEND_BUILD.version}
        </Descriptions.Item>
        <Descriptions.Item label={t('health.frontendCommit')}>
          {FRONTEND_BUILD.commit}
        </Descriptions.Item>
        <Descriptions.Item label={t('health.backendStatus')}>
          {error ? (
            <Tag color="red">{t('health.unreachable')}</Tag>
          ) : !health ? (
            <Spin size="small" />
          ) : (
            <Tag color={health.status === 'healthy' ? 'green' : 'orange'}>{health.status}</Tag>
          )}
        </Descriptions.Item>
        {health &&
          Object.entries(health.services).map(([name, status]) => (
            <Descriptions.Item key={name} label={name}>
              <Tag
                color={status === 'healthy' ? 'green' : status === 'unknown' ? 'default' : 'red'}
              >
                {status}
              </Tag>
            </Descriptions.Item>
          ))}
      </Descriptions>
    </Card>
  );
}
